import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store";
import { useNavigate } from "react-router-dom";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  UsersIcon,
  UserIcon,
  EyeIcon,
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  GlobeAltIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { Timeline as TimelineIcon } from "@mui/icons-material";
import {
  fetchCustomers,
  createCustomer,
  editCustomer,
  removeCustomer,
  // setSelectedCustomer,
  selectCustomers,
  selectCustomerLoading,
  selectCustomerError,
  selectSelectedCustomer,
  selectCustomerPagination,
} from "./customerSlice";
import { CustomerFormData, Customer } from "./customer";
import CreateCustomerModal from "./CreateCustomerModal";
import EditCustomerModal from "./EditCustomerModal";
import {
  STUB_BRANCHES,
  STUB_CUSTOMERS,
  USE_STUB_DATA,
} from "../../config/stubData";
import toast from "react-hot-toast";
import HeaderSection from "../../components/HeaderSection";
import SearchBar from "../../components/searchbar";
import { usePermission } from "../../components/common/PermissionGuard";
import { Divider, Stack } from "@mui/material";
// Removed CreateOrderPage import since we're now navigating to a new page
import ConfirmDialog from "../../components/common/ConfirmDialogue";
import SitesFormModal from "../site/SitesFormModal";
import CustumCard from "../../components/common/CustumCard";
import Pagination from "../../components/common/Pagination";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import UnifiedTimelineModal from "../../components/timeline/UnifiedTimelineModal";
import { UnifiedTimelineData } from "../../types/timeline";
import customerTimelineService from "../../services/customerTimelineService";
import PageWrapper from "../../components/common/PageWrapper";
import { SiteSidePanel, OrderSidePanel, ProductSidePanel, ItemStageSidePanel } from "../../components/common";
import CloseConfirmationDialogue from "../../components/common/__tests__/CloseConfirmationDialogue";
import { useSidePanel } from "../../hooks/useSidePanel";
import { siteService } from "../site/siteService";
import { orderService } from "../orders/orderService";
import {selectSelectedSite,setSelectedSite} from "../site/siteSlice"
import { TimelineDot } from "@mui/lab";
import { capitalizeWords } from "../../utils/formatters";

const Customers: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const customers = useAppSelector(selectCustomers);
  const pagination = useAppSelector(selectCustomerPagination);
  const loading = useAppSelector(selectCustomerLoading);
  
  // Permission checks
  const canCreateSite = usePermission('sites', 'create');
  const canCreateOrder = usePermission('orders', 'create');
  const error = useAppSelector(selectCustomerError);
  const selectedSite = useAppSelector(selectSelectedSite)
  // const selectedCustomer = useAppSelector(selectSelectedCustomer);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<"all" | "active" | "inactive">(
    "all"
  );
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [DeleteDialog, setDeleteDialog] = useState(false);
  const [deleteCustomerId, setDeleteCustomerId] = useState<string | null>(null);
  // Removed modal-related state since we're now navigating to a new page
  const [opencreateSiteModal, setOpenCreateSiteModal] = useState(false);
  const [selectCustomerId, setSelectCustomerId] = useState<string | null>(null);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineData, setTimelineData] = useState<UnifiedTimelineData | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [closeDialogSite, setCloseDialogSite] = useState(false);
  const [creatingOrderForCustomer, setCreatingOrderForCustomer] = useState<string | null>(null);
  
  // Side panel management
  const {
    // State
    showSiteSidePanel,
    showOrderSidePanel,
    showProductSidePanel,
    showItemSidePanel,
    selectedCustomerForSites,
    selectedSiteForOrders,
    selectedOrderForProducts,
    selectedProduct,
    selectedOrder,
    customerSites,
    siteOrders,
    orderProducts,
    sitesLoading,
    ordersLoading,
    productsLoading,
    
    // Close functions
    closeAll,
    closeSitePanel,
    closeOrderPanel,
    closeProductPanel,
    closeItemPanel,
    
    // Back navigation functions
    backToCustomersFromSites,
    backToSitesFromCustomerOrders,
    backToOrdersFromProducts,
    backToProductsFromItems,
    
    // Open functions
    openSitePanel,
    openOrderPanel,
    openProductPanel,
    openItemPanelFromProducts,
    
    // Loading functions
    setSitesLoadingState,
    setOrdersLoadingState,
    setProductsLoadingState,
  } = useSidePanel();
  

  useEffect(() => {
    console.log('selectedSiteForOrders: ',selectedSiteForOrders);
    console.log('selectedSite: ',selectedSite);
    console.log('selectSelectedSite: ',selectSelectedSite);
  },[selectedSiteForOrders,selectSelectedSite,selectedSite])
  

  // const city = STUB_CUSTOMERS;
  const cityOptions = ["Nashik", "Jalgaon"];
  const canEdit = usePermission("customers", "update");
  const canDelete = usePermission("customers", "delete");



  const handleTimelineView = async (customer: Customer) => {
    setTimelineLoading(true);
    try {
      // Fetch dynamic data from services
      const timelineCustomerData = await customerTimelineService.getCustomerWithTimelineData(customer.id);
      console.log("Fetched timeline data for customer:", timelineCustomerData);
      setTimelineData({
        type: 'customer',
        customer: timelineCustomerData,
      });
      setShowTimelineModal(true);
    } catch (error) {
      console.error("Failed to load timeline data:", error);
      // Show error message to user instead of fallback
      alert("Failed to load timeline data. Please try again later.");
    } finally {
      setTimelineLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    dispatch(
      fetchCustomers({
        page,
        limit: pagination.limit,
        search: searchTerm,
        status: filterType === "all" ? "" : filterType,
      })
    );
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    dispatch(
      fetchCustomers({
        page: 1, // Reset to first page when changing items per page
        limit: itemsPerPage,
        search: searchTerm,
        status: filterType === "all" ? "" : filterType,
      })
    );
  };

  // Removed scroll-based pagination in favor of proper pagination controls

  // First fetch on mount - only if customers list is empty
  useEffect(() => {
    if (customers.length === 0) {
      dispatch(
        fetchCustomers({
          page: DEFAULT_PAGINATION.page,
          limit: DEFAULT_PAGINATION.limit,
        })
      );
    }
  }, [dispatch, customers.length]);

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setSelectedCustomer(null);
    // Only refresh if we're not using stub data and actually created a customer
    if (!USE_STUB_DATA) {
      dispatch(
        fetchCustomers({
          page: pagination.page,
          limit: pagination.limit,
          search: searchTerm,
          status: filterType === "all" ? "" : filterType,
        })
      );
    }
  };

  // Handle search and filter changes - debounced to prevent excessive calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(
        fetchCustomers({
          page: DEFAULT_PAGINATION.page, // Reset to first page when filters change
          limit: DEFAULT_PAGINATION.limit,
          search: searchTerm,
          status: filterType === "all" ? "" : filterType,
        })
      );
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterType, dispatch]);

  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    console.log("Selected customer for edit:", customer);
    setIsCreateModalOpen(true);
  };

  const handleCreateOrder = async (customerId: string) => {
    // Prevent multiple clicks while creating order for this specific customer
    if (creatingOrderForCustomer === customerId) {
      console.log("🚫 Order creation already in progress for customer:", customerId);
      return;
    }

    console.log(
      "🚀 Creating order for customer ID from handlecreateorder function:",
      customerId
    );
    
    // Set loading state for this specific customer
    setCreatingOrderForCustomer(customerId);
    
    try {
      // Find the customer object by ID
      const customer = customers.find(c => c.id === customerId);
      if (!customer) {
        console.error("Customer not found with ID:", customerId);
        setCreatingOrderForCustomer(null);
        return;
      }

      // Fetch sites for this customer
      console.log("🔍 Fetching sites for customer:", customerId);
      const sitesResponse = await siteService.getSites({ customerId });
      
      if (sitesResponse.success && sitesResponse.data?.sites?.length > 0) {
        const customerSites = sitesResponse.data.sites;
        console.log("✅ Found sites for customer:", customerSites);
        
        // Navigate to order creation page with customer ID and first site ID
        const firstSite = customerSites[0];
        console.log("🚀 Navigating to order creation with customer and site:", {
          customerId,
          siteId: firstSite.id,
          customerName: customer.name,
          siteName: firstSite.name
        });
        
        navigate(`/orders/enhanced/${customerId}/${firstSite.id}`);
      } else {
        // If no sites found, navigate to order creation page with just customer ID
        console.log("⚠️ No sites found for customer, navigating with customer ID only");
        navigate(`/orders/enhanced/${customerId}`);
      }
    } catch (error) {
      console.error("❌ Error fetching sites for customer:", error);
      // Navigate to order creation page with just customer ID as fallback
      navigate(`/orders/enhanced/${customerId}`);
    } finally {
      // Reset loading state for this specific customer after navigation
      setCreatingOrderForCustomer(null);
    }
  };

  // Removed modal-related functions since we're now navigating to a new page



  const handleDeleteCustomer = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this customer?"))
      return;

    try {
      await dispatch(removeCustomer(id)).unwrap();
    } catch (error) {
      // Error is already handled by the slice
    }
  };

  const handleDeleteCutomer = (customer: Customer) => {
    dispatch(removeCustomer(customer.id));
  };

  // const filteredCustomers = customers.filter(
  //   (customer) =>
  //     customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     customer.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     customer.email.toLowerCase().includes(searchTerm.toLowerCase())
  // );

  const filteredCustomers = customers?.filter((customer) => {
    const search = searchTerm.toLowerCase();

    const matchesSearch =
      // customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      // customer.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      // customer.email.toLowerCase().includes(searchTerm.toLowerCase());
      (customer.name ?? "").toLowerCase().includes(search) ||
      (customer.code ?? "").toLowerCase().includes(search) ||
      (customer.contactEmail ?? "").toLowerCase().includes(search);

    // const ct = city.find((b) => b.city === customer.city);
    // const matchesCity = selectedCity ? ct?.city === selectedCity : true;

    return matchesSearch;
  });

  const getBranchName = (branchId: string) => {
    const branch = STUB_BRANCHES.find((b) => b.id === branchId);
    return branch?.name || "Unknown Branch";
  };

  const handleViewDetails = (customerId: string) => {
    navigate(`/customers/${customerId}`);
  };

  const handleCustomerCardClick = async (customer: Customer) => {
    console.log('Customer card clicked:', customer);
    setSitesLoadingState(true);
    
    try {
      console.log('Fetching sites for customer:', customer.id);
      const response = await siteService.getSites({ customerId: customer.id });
      console.log('Sites response:', response);
      
      if (response.success) {
        // Handle different response structures
        const sites = Array.isArray(response.data) 
          ? response.data 
          : response.data?.sites || [];
        console.log('Sites found for customer:', sites.length, sites);
        openSitePanel(customer, sites);
      } else {
        console.error('Failed to fetch sites:', response.error);
        openSitePanel(customer, []);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
      openSitePanel(customer, []);
    } finally {
      setSitesLoadingState(false);
    }
  };

  const handleSiteSelect = async (site: any) => {
    console.log('Site selected:', site);
    console.log('Selected customer for sites:', selectedCustomerForSites);
    setOrdersLoadingState(true);
    
    try {
      console.log('Fetching orders for site ID:', site.id);
      const response = await orderService.getOrders({ siteId: site.id });
      console.log('Orders response:', response);
      
      if (response.success) {
        const ordersData = response.data;
        const orders = ordersData?.orders || [];
        console.log('Orders for site:', orders.length, orders);
        
        openOrderPanel(site, orders);
      } else {
        console.error('Failed to fetch orders:', response.error);
        openOrderPanel(site, []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      openOrderPanel(site, []);
    } finally {
      setOrdersLoadingState(false);
    }
  };

  const handleOrderSelect = async (order: any) => {
    console.log('Order selected:', order);
    setProductsLoadingState(true);
    
    try {
      console.log('Fetching full order details for:', order.id);
      const response = await orderService.getOrder(order.id);
      console.log('Order details response:', response);
      
      if (response.success) {
        const fullOrder = response.data;
        console.log('Full order data:', fullOrder);
        
        if (fullOrder.products && fullOrder.products.length > 0) {
          console.log('Order products found:', fullOrder.products.length, fullOrder.products);
          openProductPanel(order, fullOrder.products);
        } else {
          console.log('No products found in order, using empty array');
          openProductPanel(order, []);
        }
      } else {
        console.error('Failed to fetch order details:', response.error);
        openProductPanel(order, []);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      openProductPanel(order, []);
    } finally {
      setProductsLoadingState(false);
    }
  };

  const handleProductSelect = (product: any) => {
    console.log('Product selected:', product);
    openItemPanelFromProducts(product, selectedOrderForProducts);
  };

  const handleItemSelect = (item: any) => {
    console.log('Item selected:', item);
    // Additional logic for item selection can be added here
  };

  const handleStageSelect = (item: any, stage: any) => {
    console.log('Stage selected:', item, stage);
    // Handle stage selection if needed
  };

  const handleBackToProducts = () => {
    console.log('Back to products clicked');
    backToProductsFromItems();
  };

  const handleBackToOrders = () => {
    console.log('Back to orders clicked');
    backToOrdersFromProducts();
  };

  const handleBackToSites = () => {
    console.log('Back to sites clicked');
    backToSitesFromCustomerOrders();
  };

  const handleBackToCustomers = () => {
    console.log('Back to customers clicked');
    backToCustomersFromSites();
  };

  // Error display is handled by Redux slice and errorHandlingService
  // No need to show toast here as it causes duplicate error messages

  // const cityOptions = Array.from(new Set(city.map((b) => b.city)));

  const handleCreateSite = (customerId: string) => {
    setSelectCustomerId(customerId);
    console.log(
      "Creating site for customer ID from handleCreateSite function:",
      selectCustomerId
    );
    setOpenCreateSiteModal(true);
  };

  const handleSiteCreated = (site: any) => {
    console.log("Site created successfully from Customers component:", site);
    // Close the create modal
    setOpenCreateSiteModal(false);
    setSelectCustomerId(null);
    // Refresh the customers list to show updated data
    dispatch(fetchCustomers({}));
  };

  return (
    <PageWrapper >
      <HeaderSection
        title="Customers"
        description="Manage your customer database and their information"
        icon={
          <UsersIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
        }
        buttonIcon={<PlusIcon className="h-5 w-5 mr-2" />}
        buttonText="Add Customer"
        onButtonClick={() => {
          console.log("Add Customer button clicked");
          setSelectedCustomer(null);
          setIsCreateModalOpen(true);
        }}
        
      />

      <div className="mb-6 flex justify-between items-center">
        <div className="w-64 flex-1">
          <SearchBar
            placeholder="Search customers..."
            value={searchTerm}
            // onChange={setSearchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {/* <div className="flex mb-4 ml-3">
          <select
            className="block w-48 pl-3 pr-10 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            value={selectedCity || ""}
            onChange={(e) => setSelectedCity(e.target.value || null)}
          >
            <option value="">All Cities</option>
            {cityOptions.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div> */}
      </div>
      {/* <Stack spacing={2}> */}

      {customers.length === 0 ? (
        <div className="p-6 text-center rounded-xl shadow-md bg-white dark:bg-secondary-800">
          <h2 className="text-lg font-semibold mb-2">
            {loading ? "Loading customers..." : "Customers not available"}
          </h2>
          <button
            onClick={() => dispatch(fetchCustomers({}) as any)}
            className="mt-2 px-5 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold shadow-md hover:shadow-lg transition"
          >
            {/* <PlusIcon className="h-5 w-5 mr-2 inline" /> */}
            Retry
          </button>
        </div>
      ) : (
        <div>
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
              {filteredCustomers.map((customer) => (
                <div
                  key={`${customer.id}-${customer.status}`}
                  className="flex flex-col justify-between hover:shadow-lg transition-shadow cursor-pointer bg-white dark:bg-gray-900 rounded-2xl shadow-md pt-2 h-full"
                  onClick={() => handleCustomerCardClick(customer)}
                >
                  {/* Top Content */}
                  <div className="flex-1 flex flex-col justify-start ml-1 pr-4 pl-4">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      {/* Left: Avatar + Name */}
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center flex-shrink-0">
                          <UserIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                        </div>

                        <h3
                          className="text-lg font-semibold text-gray-900 dark:text-white truncate "
                          title={customer.name}
                        >
                          {capitalizeWords(customer.name) || ""}
                        </h3>

                        <span>
                          {customer.status ? (
                            <TimelineDot
                              sx={{ width: 8, height: 8, p: 0, m: 0 }}
                              variant="filled"
                              color="success"
                            />
                          ) : (
                            <TimelineDot
                              sx={{ width: 8, height: 8, p: 0, m: 0 }}
                              variant="filled"
                              color="error"
                            />
                          )}
                        </span>
                      </div>

                      {/* Right: Edit button */}
                      <div className="flex justify-end gap-2 flex-shrink-0">
                        {canEdit && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCustomer(customer);
                            }}
                            className="flex ml-2 items-center justify-center py-3 text-sm text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/10 transition-colors"
                            disabled={loading}
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Info section */}
                    <div className="space-y-3">
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <MapPinIcon className="h-5 w-5 mr-2" />
                        <span className="truncate" title={customer.city}>
                          {customer.city || "N/A"}
                        </span>
                      </div>

                      <div className="flex flex-wrap justify-between gap-2">
                        {/* Phone */}
                        <div
                          className="flex items-center text-sm text-gray-500 dark:text-gray-400"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <PhoneIcon className="h-5 w-5 mr-2" />
                          {customer.phone ? (
                            <a
                              href={`tel:${customer.phone}`}
                              className="hover:text-blue-600 truncate block max-w-[140px]"
                              title={customer.phone}
                            >
                              {customer.phone}
                            </a>
                          ) : (
                            "Not available"
                          )}
                        </div>

                        {/* Email */}
                        <div
                          className="flex items-center text-sm text-gray-500 dark:text-gray-400"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <EnvelopeIcon className="h-5 w-5 mr-2" />
                          {customer.contactEmail ? (
                            <a
                              href={`mailto:${customer.contactEmail}`}
                              className="hover:text-blue-600 truncate block max-w-[160px]"
                              title={customer.contactEmail}
                            >
                              {customer.contactEmail}
                            </a>
                          ) : (
                            "Not available"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Buttons */}
                  <div className="mt-3">
                    <Divider className="dark:border-gray-600" />
                    <div className="grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-600 h-12">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateSite(customer.id);
                        }}
                        className={`flex items-center justify-center px-4 py-1 text-sm transition-colors h-full hover:rounded-bl-2xl ${
                          !canCreateSite || loading
                            ? 'text-gray-400 cursor-not-allowed bg-gray-100 dark:bg-gray-800 dark:text-gray-600'
                            : 'text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/10'
                        }`}
                        disabled={!canCreateSite || loading}
                      >
                        Create Site
                      </button>

                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log(
                            'Place Order button clicked for customer:',
                            customer.id
                          );
                          handleCreateOrder(customer.id);
                        }}
                        disabled={!canCreateOrder || creatingOrderForCustomer === customer.id || loading}
                        className={`flex items-center justify-center px-4 py-3 text-sm transition-colors h-full hover:rounded-br-2xl ${
                          !canCreateOrder || creatingOrderForCustomer === customer.id || loading
                            ? 'text-gray-400 cursor-not-allowed bg-gray-100 dark:bg-gray-800 dark:text-gray-600'
                            : 'text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/10'
                        }`}
                      >
                        {creatingOrderForCustomer === customer.id
                          ? 'Creating...'
                          : 'Place Order'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      )}
      
      {opencreateSiteModal && (
        <SitesFormModal
          isOpen={opencreateSiteModal}
          // onClose={() => setCloseDialogSite(true)}
          onRequestClose={(hasData) => {
            if (hasData) {
              setCloseDialogSite(true);
            } else {
              setOpenCreateSiteModal(false);
              setSelectCustomerId(null);
            }
          }}
          sites={null}
          customerId={selectCustomerId || ""}
          onSuccess={handleSiteCreated}
        />
      )}

      {closeDialogSite && (
        <CloseConfirmationDialogue
          open={closeDialogSite}
          onClose={() => setCloseDialogSite(false)}
          onConfirm={() => {
            setCloseDialogSite(false);
            setOpenCreateSiteModal(false);
            setSelectCustomerId(null);
          }}
          title=""
          message="Are you sure you want to close this site model?"
        />
      )}

      {DeleteDialog && (
        <ConfirmDialog
          open={DeleteDialog}
          onClose={() => setDeleteDialog(false)}
          onConfirm={() => {
            if (deleteCustomerId) {
              const customerToDelete = customers.find(c => c.id === deleteCustomerId);
              if (customerToDelete) {
                handleDeleteCutomer(customerToDelete);
              }
            }
            setDeleteDialog(false);
            setDeleteCustomerId(null);
          }}
          title="Delete Site"
          message="Are you sure you want to delete this site?"
        />
      )}

      {closeDialog && (
        <CloseConfirmationDialogue
          open={closeDialog}
          onClose={() => setCloseDialog(false)}
          onConfirm={() => {
            setCloseDialog(false);
            setIsCreateModalOpen(false);
            setSelectedCustomer(null);
          }}
          title=""
          message="Are you sure you want to close this customer modal?"
        />
      )}

      {/* Customer list */}
      {/* {customers.map((customer) => (
        <div key={customer.id}>{customer.name}</div>
      ))} */}

      {/* Pagination */}
      {pagination.total > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.pages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showItemsPerPage={true}
        />
      )}

      {/* <PlaceOrderModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        siteData={selectedOrder || ""}
        selectedOrderId={selectedOrderId || ""}
      /> */}
      {/* Removed CreateOrderPage modal since we're now navigating to a new page */}

      {/* Timeline Modal */}
      {timelineData && (
        <UnifiedTimelineModal
          isOpen={showTimelineModal}
          onClose={() => {
            setShowTimelineModal(false);
            setTimelineData(null);
          }}
          data={timelineData}
        />
      )}

      {/* {isCreateModalOpen && ( */}
        <CreateCustomerModal
          isOpen={isCreateModalOpen}
          onRequestClose={(hasData) => {
            if (hasData) {
              setCloseDialog(true);
            } else {
              setIsCreateModalOpen(false);
              setSelectedCustomer(null);
            }
          }}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            setSelectedCustomer(null);
            // Only refresh if we're not using stub data and actually created a customer
            if (!USE_STUB_DATA) {
              dispatch(
                fetchCustomers({
                  page: pagination.page,
                  limit: pagination.limit,
                  search: searchTerm,
                  status: filterType === "all" ? "" : filterType,
                })
              );
            }
          }}
          customer={selectedCustomer}

        />
      {/* )} */}

      {/* Site Side Panel */}
      <SiteSidePanel
        isOpen={showSiteSidePanel}
        onClose={closeSitePanel}
        onCloseAll={closeAll}
        sites={customerSites}
        onSiteSelect={handleSiteSelect}
        title={selectedCustomerForSites ? `${selectedCustomerForSites.name} - Sites` : undefined}
        loading={sitesLoading}
        customer={selectedCustomerForSites ? {
          id: selectedCustomerForSites.id,
          name: selectedCustomerForSites.name,
          email: selectedCustomerForSites.email,
          phone: selectedCustomerForSites.phone,
          address: selectedCustomerForSites.address,
          city: selectedCustomerForSites.city,
          state: selectedCustomerForSites.state
        } : undefined}
        onBackToCustomers={handleBackToCustomers}
        selectedSiteId={selectedSiteForOrders?.id}
      />

      {/* Order Side Panel */}
      
          <OrderSidePanel
        isOpen={showOrderSidePanel}
        onClose={closeOrderPanel}
        onCloseAll={closeAll}
        orders={siteOrders}
        onOrderSelect={handleOrderSelect}
        title={selectedSiteForOrders ? `${selectedSiteForOrders.name}` : undefined}
        loading={ordersLoading}
        onBackToSites={handleBackToSites}
        selectedOrderId={selectedOrderForProducts?.id}
        site={selectedSiteForOrders}
      />
     
      

      {/* Product Side Panel */}
      <ProductSidePanel
        isOpen={showProductSidePanel}
        onClose={closeProductPanel}
        onCloseAll={closeAll}
        products={orderProducts}
        onProductSelect={handleProductSelect}
        title={selectedOrderForProducts ? `Order ${selectedOrderForProducts.orderNumberNew || selectedOrderForProducts.orderNumber || selectedOrderForProducts.order_tracking_code || selectedOrderForProducts.id} - Sets` : undefined}
        loading={productsLoading}
        onBackToOrders={handleBackToOrders}
        selectedProductId={selectedProduct?.id}
      />

      {/* Item/Stage Side Panel */}
      <ItemStageSidePanel
        isOpen={showItemSidePanel}
        onClose={closeItemPanel}
        onCloseAll={closeAll}
        product={selectedProduct}
        onItemSelect={handleItemSelect}
        onStageSelect={handleStageSelect}
        onBackToProducts={handleBackToProducts}
        title={selectedProduct ? `${selectedProduct.name || selectedProduct.type} - Parts` : undefined}
        order={selectedOrderForProducts || selectedOrder}
      />

    </PageWrapper>
  );
};

export default Customers;
